import React from "react";
import { ProgressSpinner } from "primereact/progressspinner";

import { apiErrorDetail } from "../../../api/errors";
import { formatCurrency } from "../policy-application/util";
import { useEndorsementSovDiff } from "./data";
import styles from "./Endorsements.module.css";

const DiffSection: React.FC<{
  label: string;
  count: number;
  defaultOpen: boolean;
  children: React.ReactNode;
}> = ({ label, count, defaultOpen, children }) => (
  <details className={styles.diffSection} open={defaultOpen && count > 0}>
    <summary className={styles.diffSummary}>
      {label} ({count})
    </summary>
    {count > 0 ? (
      <ul className={styles.diffList}>{children}</ul>
    ) : (
      <span className={styles.premiumHint}>None</span>
    )}
  </details>
);

/** Read-only added / removed / revalued view of an endorsement's generated
 * schedule against its baseline, with the agent's own summary of the run,
 * for clearing review. */
export const EndorsementSovDiffPanel: React.FC<{
  endorsementId: string;
  summary: string | null;
}> = ({ endorsementId, summary }) => {
  const diff = useEndorsementSovDiff(endorsementId);

  if (diff.isLoading) {
    return (
      <div className={styles.diffPanel}>
        <ProgressSpinner style={{ width: 24, height: 24 }} />
      </div>
    );
  }
  if (diff.error || !diff.data) {
    return (
      <div className={styles.diffPanel}>
        <span className={styles.premiumHint}>
          {apiErrorDetail(diff.error) ?? "Could not load the schedule diff."}
        </span>
      </div>
    );
  }

  const { baseline_description, added, removed, revalued, unchanged_count } =
    diff.data;
  return (
    <div className={styles.diffPanel}>
      {summary && <blockquote className={styles.summary}>{summary}</blockquote>}
      <span className={styles.premiumHint}>
        Endorsed schedule {baseline_description} · {unchanged_count} location
        {unchanged_count === 1 ? "" : "s"} unchanged. Locations match on
        geocoded coordinates first, then on address text; rows sharing one
        address compare as one. Two caveats: an unmatched reworded address shows
        as an add + remove pair, and locations with the same street number and
        identical TIV are treated as the same location.
      </span>
      <DiffSection label="Added" count={added.length} defaultOpen>
        {added.map((loc, index) => (
          <li key={`${index}-${loc.address}`}>
            <span className={styles.diffAddress}>{loc.address}</span>
            <span className={styles.positiveDelta}>
              +{formatCurrency(loc.tiv)}
            </span>
          </li>
        ))}
      </DiffSection>
      <DiffSection label="Removed" count={removed.length} defaultOpen>
        {removed.map((loc, index) => (
          <li key={`${index}-${loc.address}`}>
            <span className={styles.diffAddress}>{loc.address}</span>
            <span className={styles.negativeDelta}>
              −{formatCurrency(loc.tiv)}
            </span>
          </li>
        ))}
      </DiffSection>
      <DiffSection label="Revalued" count={revalued.length} defaultOpen={false}>
        {revalued.map((loc, index) => (
          <li key={`${index}-${loc.address}`}>
            <span className={styles.diffAddress}>{loc.address}</span>
            <span>
              {formatCurrency(loc.tiv_before)} → {formatCurrency(loc.tiv_after)}
            </span>
          </li>
        ))}
      </DiffSection>
    </div>
  );
};
